import { memo, useEffect, useRef, useState } from 'react';
import { AlertTriangle, ArrowDown, CloudOff, Clock3, RotateCw, ShieldAlert, Trash2 } from 'lucide-react';
import { Button, cn } from '@cyphex/ui';
import type { Message } from '@cyphex/types';
import type { ConnectionState } from '../../api';
import { formatSchedule } from '../../lib/time';
import type { OutboxItem } from '../../types';

interface OfflineBannerProps {
  connection: ConnectionState;
  queued: number;
}

/** How long a wobble is allowed to last before it is worth telling anyone. */
const SETTLE_MS = 1500;

/**
 * Honest offline state — queued sends are safe and will flush on reconnect.
 *
 * Two rules keep it honest rather than merely loud:
 *
 *   • A FIRST connection is not a RE-connection. Every page load starts in
 *     `connecting`, so the banner greeted every hard refresh with
 *     "Reconnecting…" — a warning about a failure that had not happened. It now
 *     stays quiet until a connection has actually been established and lost.
 *
 *   • A wobble shorter than a second and a half is not news. A socket that
 *     blips and recovers used to flash a warning bar and shove the whole
 *     timeline down a row on its way past.
 *
 * A genuine outage still says so, immediately: `offline` is never delayed.
 */
export const OfflineBanner = memo(function OfflineBanner({ connection, queued }: OfflineBannerProps) {
  const hasConnected = useRef(false);
  const [settled, setSettled] = useState(false);

  if (connection === 'online') hasConnected.current = true;

  useEffect(() => {
    if (connection !== 'connecting') {
      setSettled(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setSettled(true), SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [connection]);

  if (connection === 'online') return null;
  /* Still opening the very first socket: say nothing. */
  if (connection === 'connecting' && (!hasConnected.current || !settled)) return null;
  return (
    <div className="flex items-center gap-2 border-b border-warning/30 bg-warning-subtle px-4 py-1.5 text-sm text-warning-foreground">
      <CloudOff size={14} aria-hidden />
      {connection === 'connecting' ? 'Reconnecting…' : 'You are offline.'}
      {queued > 0 ? ` ${queued} message${queued === 1 ? '' : 's'} will send automatically.` : ''}
    </div>
  );
});

interface FailedSendBarProps {
  failed: readonly OutboxItem[];
  onRetryAll: () => void;
  onDiscardAll: () => void;
}

/**
 * Sends that exhausted their retries.
 *
 * A per-bubble Retry already exists, but a message that failed while the user
 * was scrolled elsewhere is invisible — and a chat app that silently loses a
 * message is worse than one that never accepted it. This strip is the "you
 * have unsent messages" guarantee; the bubble affordance stays for precision.
 */
export const FailedSendBar = memo(function FailedSendBar({
  failed,
  onRetryAll,
  onDiscardAll,
}: FailedSendBarProps) {
  if (failed.length === 0) return null;
  return (
    <div
      role="alert"
      className="flex items-center gap-2 border-t border-destructive/30 bg-destructive/10 px-4 py-1.5 text-sm text-destructive"
    >
      <AlertTriangle size={14} aria-hidden />
      <span className="min-w-0 flex-1 truncate">
        {failed.length} message{failed.length === 1 ? '' : 's'} could not be sent.
      </span>
      <Button variant="link" size="xs" leftIcon={RotateCw} onClick={onRetryAll}>
        Retry all
      </Button>
      <Button variant="link" size="xs" leftIcon={Trash2} onClick={onDiscardAll}>
        Discard all
      </Button>
    </div>
  );
});

interface ChatRequestBarProps {
  senderName: string;
  onAccept: () => void;
  onBlock: () => void;
}

/** Message-request gate shown above the composer until the user decides. */
export const ChatRequestBar = memo(function ChatRequestBar({
  senderName,
  onAccept,
  onBlock,
}: ChatRequestBarProps) {
  return (
    <div className="flex items-center gap-3 border-t border-border bg-card px-4 py-3">
      <ShieldAlert size={20} className="shrink-0 text-warning" aria-hidden />
      <p className="min-w-0 flex-1 text-md text-secondary-foreground">
        <span className="font-semibold text-foreground">{senderName}</span> wants to message you. Accept
        to reply and show your read receipts.
      </p>
      <Button variant="danger-outline" size="sm" onClick={onBlock}>
        Block
      </Button>
      <Button variant="primary" size="sm" onClick={onAccept}>
        Accept
      </Button>
    </div>
  );
});

interface JumpToLatestProps {
  visible: boolean;
  unread: number;
  onClick: () => void;
}

export const JumpToLatest = memo(function JumpToLatest({ visible, unread, onClick }: JumpToLatestProps) {
  if (!visible) return null;
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center">
      <Button
        variant="primary"
        size="sm"
        pill
        leftIcon={ArrowDown}
        onClick={onClick}
        className="pointer-events-auto shadow-raised"
      >
        {unread > 0 ? `${unread} new message${unread === 1 ? '' : 's'}` : 'Jump to latest'}
      </Button>
    </div>
  );
});

interface ScheduledStripProps {
  scheduled: readonly Message[];
  onCancel: (message: Message) => void;
  className?: string;
}

/** "2 scheduled messages" strip, matching the `/scheduled` endpoint. */
export const ScheduledStrip = memo(function ScheduledStrip({
  scheduled,
  onCancel,
  className,
}: ScheduledStripProps) {
  const next = scheduled[0];
  if (!next) return null;
  return (
    <div
      className={cn(
        'flex items-center gap-2 border-t border-border bg-primary-subtle px-4 py-1.5 text-sm text-primary',
        className,
      )}
    >
      <Clock3 size={14} aria-hidden />
      <span className="min-w-0 flex-1 truncate">
        {scheduled.length} scheduled · next {formatSchedule(next.scheduledFor ?? next.createdAt)}
      </span>
      <Button variant="link" size="xs" onClick={() => onCancel(next)}>
        Cancel
      </Button>
    </div>
  );
});
